import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@travellegacy/nexops-design-system';
import {
  SortableTable,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import {
  useSuggestions,
  useCreateSuggestion,
  useUpdateSuggestionStatus,
  type Suggestion,
} from '../hooks/useSuggestions';
import { useAuth } from '@/shared/contexts/AuthContext';
import { formatDate } from '@/shared/lib/utils';
import { Loader2, Send, Lightbulb, AlertCircle } from 'lucide-react';

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' }> = {
  PENDING: { label: 'Pendente', variant: 'default' },
  ANALYZING: { label: 'Em Análise', variant: 'warning' },
  IN_PROGRESS: { label: 'Em Desenvolvimento', variant: 'warning' },
  IMPLEMENTED: { label: 'Implementado', variant: 'success' },
  REJECTED: { label: 'Rejeitado', variant: 'error' },
  ON_HOLD: { label: 'Em Espera', variant: 'default' },
};

const TYPE_LABELS = {
  SUGGESTION: { label: 'Sugestão', icon: Lightbulb, color: 'text-blue-500' },
  PROBLEM: { label: 'Problema', icon: AlertCircle, color: 'text-red-500' },
};

export default function SuggestionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // Formulário
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'SUGGESTION' as 'SUGGESTION' | 'PROBLEM',
    module: '',
  });

  // Filtros
  const [typeFilter, setTypeFilter] = useState<'all' | 'SUGGESTION' | 'PROBLEM'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal de conclusão
  const [conclusionModal, setConclusionModal] = useState<{
    isOpen: boolean;
    suggestionId: string;
    status: string;
    conclusion: string;
  }>({
    isOpen: false,
    suggestionId: '',
    status: '',
    conclusion: '',
  });

  // Modal de detalhes
  const [detailsModal, setDetailsModal] = useState<{
    isOpen: boolean;
    suggestion: Suggestion | null;
  }>({
    isOpen: false,
    suggestion: null,
  });

  // Hooks
  const { data, isLoading } = useSuggestions({
    type: typeFilter === 'all' ? undefined : typeFilter,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const createMutation = useCreateSuggestion();
  const updateStatusMutation = useUpdateSuggestionStatus();

  const suggestions = data?.suggestions || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData, {
      onSuccess: () => {
        setFormData({
          title: '',
          description: '',
          type: 'SUGGESTION',
          module: '',
        });
      },
    });
  };

  const handleStatusChange = (id: string, newStatus: string) => {
    // Se o status for IMPLEMENTED ou REJECTED, abrir modal para pedir conclusão
    if (newStatus === 'IMPLEMENTED' || newStatus === 'REJECTED') {
      setConclusionModal({
        isOpen: true,
        suggestionId: id,
        status: newStatus,
        conclusion: '',
      });
    } else {
      // Outros status não precisam de conclusão
      updateStatusMutation.mutate({ id, status: newStatus });
    }
  };

  const handleConclusionSubmit = () => {
    if (conclusionModal.conclusion.trim().length < 10) {
      return;
    }

    updateStatusMutation.mutate({
      id: conclusionModal.suggestionId,
      status: conclusionModal.status,
      conclusion: conclusionModal.conclusion,
    });

    setConclusionModal({
      isOpen: false,
      suggestionId: '',
      status: '',
      conclusion: '',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Central de Sugestões</h1>
        <p className="text-muted-foreground mt-2">
          {isAdmin
            ? 'Visualize e gerencie todas as sugestões e problemas reportados'
            : 'Envie sugestões de melhorias ou reporte problemas no sistema'}
        </p>
      </div>

      {/* Formulário - Apenas para não-admin */}
      {!isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Problemas e Sugestões</CardTitle>
            <CardDescription>
              Compartilhe suas ideias ou nos avise sobre problemas que encontrou
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Tipo *"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'SUGGESTION' | 'PROBLEM' })}
                  required
                  options={[
                    { value: 'SUGGESTION', label: '💡 Sugestão' },
                    { value: 'PROBLEM', label: '⚠️ Problema' },
                  ]}
                />

                <Select
                  label="Módulo *"
                  value={formData.module}
                  onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                  required
                  options={[
                    { value: 'Financeiro', label: 'Financeiro' },
                    { value: 'Automações', label: 'Automações' },
                    { value: 'Núcleo de Pessoas', label: 'Núcleo de Pessoas' },
                    { value: 'Operações com Cliente', label: 'Operações com Cliente' },
                    { value: 'Grupos WhatsApp', label: 'Grupos WhatsApp' },
                    { value: 'Central de Problemas', label: 'Central de Problemas' },
                    { value: 'Instagram', label: 'Instagram' },
                    { value: 'Blog', label: 'Blog' },
                    { value: 'Feedback e PDI', label: 'Feedback e PDI' },
                    { value: 'Outros', label: 'Outros' },
                  ]}
                />
              </div>

              <Input
                label="Título *"
                required
                placeholder="Resuma em poucas palavras..."
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                minLength={5}
                maxLength={200}
              />

              <Textarea
                label="Descrição *"
                required
                placeholder="Descreva com detalhes..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                minLength={10}
                rows={4}
                className="resize-none"
              />

              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Enviar
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="space-y-2 flex-1">
          <label className="text-sm font-medium">Tipo</label>
          <TabFilter
            tabs={[
              { value: 'all', label: 'Todos' },
              { value: 'SUGGESTION', label: 'Sugestões' },
              { value: 'PROBLEM', label: 'Problemas' },
            ]}
            currentTab={typeFilter}
            onChange={(value) => setTypeFilter(value as 'all' | 'SUGGESTION' | 'PROBLEM')}
          />
        </div>

        <div className="flex-1">
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'PENDING', label: 'Pendente' },
              { value: 'ANALYZING', label: 'Em Análise' },
              { value: 'IN_PROGRESS', label: 'Em Desenvolvimento' },
              { value: 'IMPLEMENTED', label: 'Implementado' },
              { value: 'REJECTED', label: 'Rejeitado' },
              { value: 'ON_HOLD', label: 'Em Espera' },
            ]}
          />
        </div>
      </div>

      {/* Lista de Sugestões */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nenhuma sugestão encontrada</p>
            </div>
          ) : (
            <SortableTable
              data={suggestions}
              defaultSortKey="createdAt"
              defaultSortDirection="desc"
            >
              {(sortedSuggestions) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead sortable sortKey="status">Status</TableHead>
                      <TableHead sortable sortKey="type">Tipo</TableHead>
                      <TableHead sortable sortKey="title">Título</TableHead>
                      <TableHead sortable sortKey="module">Módulo</TableHead>
                      <TableHead sortable sortKey="createdAt">Data</TableHead>
                      <TableHead sortable sortKey="conclusionDate">Conclusão</TableHead>
                      {isAdmin && <TableHead sortable sortKey="user.name">Usuário</TableHead>}
                      {isAdmin && <TableHead>Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSuggestions.map((suggestion: Suggestion) => {
                      const TypeIcon = TYPE_LABELS[suggestion.type].icon;
                      return (
                        <TableRow
                          key={suggestion.id}
                          onClick={() => setDetailsModal({ isOpen: true, suggestion })}
                          className="cursor-pointer"
                        >
                          <TableCell>
                            <Badge variant={STATUS_LABELS[suggestion.status]?.variant || 'default'}>
                              {STATUS_LABELS[suggestion.status]?.label || suggestion.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <TypeIcon className={`h-4 w-4 ${TYPE_LABELS[suggestion.type].color}`} />
                              <span className="text-sm">{TYPE_LABELS[suggestion.type].label}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-medium">{suggestion.title}</p>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {suggestion.description}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{suggestion.module}</Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(suggestion.createdAt)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {suggestion.conclusionDate ? (
                              <span className="text-sm text-muted-foreground">
                                {formatDate(suggestion.conclusionDate)}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <div>
                                <p className="text-sm font-medium">{suggestion.user.name}</p>
                                <p className="text-xs text-muted-foreground">{suggestion.user.email}</p>
                              </div>
                            </TableCell>
                          )}
                          {isAdmin && (
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Select
                                value={suggestion.status}
                                onChange={(e) => handleStatusChange(suggestion.id, e.target.value)}
                                options={[
                                  { value: 'PENDING', label: 'Pendente' },
                                  { value: 'ANALYZING', label: 'Em Análise' },
                                  { value: 'IN_PROGRESS', label: 'Em Desenvolvimento' },
                                  { value: 'IMPLEMENTED', label: 'Implementado' },
                                  { value: 'REJECTED', label: 'Rejeitado' },
                                  { value: 'ON_HOLD', label: 'Em Espera' },
                                ]}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </SortableTable>
          )}
        </CardContent>
      </Card>

      {/* Modal de Conclusão */}
      <Dialog open={conclusionModal.isOpen} onOpenChange={(open) => !open && setConclusionModal({ ...conclusionModal, isOpen: false })}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {conclusionModal.status === 'IMPLEMENTED' ? 'Conclusão - Implementado' : 'Conclusão - Rejeitado'}
            </DialogTitle>
            <DialogDescription>
              {conclusionModal.status === 'IMPLEMENTED'
                ? 'Explique o que foi implementado para que o usuário entenda a solução.'
                : 'Explique o motivo da rejeição para que o usuário entenda a decisão.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-4 sm:px-6">
            <Textarea
              label="Conclusão *"
              placeholder="Descreva com detalhes..."
              value={conclusionModal.conclusion}
              onChange={(e) => setConclusionModal({ ...conclusionModal, conclusion: e.target.value })}
              minLength={10}
              rows={5}
              required
            />
            <p className="text-sm text-muted-foreground">
              Mínimo 10 caracteres
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConclusionModal({ ...conclusionModal, isOpen: false })}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConclusionSubmit}
              disabled={conclusionModal.conclusion.trim().length < 10 || updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Confirmar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes */}
      <Dialog open={detailsModal.isOpen} onOpenChange={(open) => !open && setDetailsModal({ isOpen: false, suggestion: null })}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Detalhes da Solicitação</DialogTitle>
          </DialogHeader>

          {detailsModal.suggestion && (
            <div className="space-y-6 px-4 sm:px-6 pb-4">
              {/* Status e Tipo */}
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <Badge variant={STATUS_LABELS[detailsModal.suggestion.status]?.variant || 'default'}>
                    {STATUS_LABELS[detailsModal.suggestion.status]?.label || detailsModal.suggestion.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Tipo</p>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const TypeIcon = TYPE_LABELS[detailsModal.suggestion.type].icon;
                      return (
                        <>
                          <TypeIcon className={`h-4 w-4 ${TYPE_LABELS[detailsModal.suggestion.type].color}`} />
                          <span className="text-sm font-medium">{TYPE_LABELS[detailsModal.suggestion.type].label}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Módulo</p>
                  <Badge variant="outline">{detailsModal.suggestion.module}</Badge>
                </div>
              </div>

              {/* Título */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Título</p>
                <p className="font-medium text-lg">{detailsModal.suggestion.title}</p>
              </div>

              {/* Descrição */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Descrição</p>
                <p className="text-sm whitespace-pre-wrap">{detailsModal.suggestion.description}</p>
              </div>

              {/* Conclusão */}
              {detailsModal.suggestion.conclusion && (
                <div className="p-4 bg-muted rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-semibold text-foreground">
                      {detailsModal.suggestion.status === 'IMPLEMENTED' ? '✅ Implementado' : '❌ Rejeitado'}
                    </p>
                    {detailsModal.suggestion.conclusionDate && (
                      <span className="text-sm text-muted-foreground">
                        • {formatDate(detailsModal.suggestion.conclusionDate)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{detailsModal.suggestion.conclusion}</p>
                </div>
              )}

              {/* Informações adicionais */}
              <div className="flex items-center gap-6 text-sm text-muted-foreground pt-4 border-t">
                <div>
                  <span className="font-medium">Criado em:</span> {formatDate(detailsModal.suggestion.createdAt)}
                </div>
                {isAdmin && (
                  <div>
                    <span className="font-medium">Por:</span> {detailsModal.suggestion.user.name}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setDetailsModal({ isOpen: false, suggestion: null })}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
